import CAButton from '@ds/components/buttons/CAButton'
import CAInput from '@ds/components/form-elements/CAInput'
import CAIcon from '@ds/components/shared/CAIcon'
import { PER_PAGE } from '@vuejs/helpers/_constants'
import { defineComponent, type PropType } from 'vue'

import DataTable, {
	type ColumnData,
	type RowData,
	type TableData,
} from './DataTable'
import LoadingIcon from './LoadingIcon'

export default defineComponent({
	name: 'MoveDataBetweenDataTables',

	props: {
		buttonText: { default: '', type: String },
		cellDataArray: { required: true, type: Array as PropType<string[]> },
		columns: { required: true, type: Array as PropType<ColumnData[]> },
		data: { required: true, type: Array as PropType<any[]> },
		dataType: { default: '', type: String },
		filterText: { default: 'Filter', type: String },
		inputId: { required: true, type: String },
		isLoading: { default: false, type: Boolean },
		isStickyHeader: { default: true, type: Boolean },
		itemsPerPage: { default: PER_PAGE.ITEMS, type: Number },
		rows: { required: true, type: Array as PropType<RowData[]> },
		selectedRows: { required: true, type: Array as PropType<RowData[]> },
		showCheckboxes: { default: true, type: Boolean },
		showPagination: { default: true, type: Boolean },
		sortable: { default: true, type: Boolean },
	},

	emits: {
		remove: (_selectedData: RowData[]) => true,
		select: (_selectedData: RowData[]) => true,
	},

	data(): Data {
		return {
			searchInput: '',
			selectedAddIds: [],
			selectedData: {
				columns: this.columns,
				rows: [],
			},

			selectedRemoveIds: [],
		}
	},

	computed: {
		selectedTableData(): TableData {
			return {
				columns: this.columns,
				rows: this.selectedRows,
			}
		},

		tableData(): TableData {
			return {
				columns: this.columns,
				rows: this.rows.filter(row => !this.selectedRows.some(selectedRow => selectedRow.rowId === row.rowId)),
			}
		},
	},

	methods: {
		addSelected() {
			const newSelectedRows = this.rows.filter(row => this.selectedAddIds.includes(row.rowId))

			this.$emit('select', newSelectedRows)

			this.selectedAddIds = []

			this.setDomInputValueToSelectedDataIds()
		},

		addSelectedDataItems(ids: string[]) {
			this.selectedAddIds = ids
		},

		removeSelected() {
			const removingSelectedRows = this.selectedRows.filter(row => this.selectedRemoveIds.includes(row.rowId))

			this.$emit('remove', removingSelectedRows)

			this.selectedRemoveIds = []

			this.setDomInputValueToSelectedDataIds()
		},

		removeSelectedDataItems(ids: string[]) {
			this.selectedRemoveIds = ids
		},

		setDomInputValueToSelectedDataIds() {
			const inputElement = document.getElementById(this.inputId)! as HTMLSelectElement

			if (!inputElement) {
				console.warn(`Element with id ${this.inputId} not found.`)

				return
			}

			const optionsArray = Array.from(inputElement.options)
			const selectedIds = this.selectedRows.map(i => i.rowId)

			for (const option of optionsArray) {
				option.selected = selectedIds.includes(option.value)
			}
		},
	},

	render() {
		return (
			<div class="move-data-between-tables" data-test="move-data-between-tables">
				{this.isLoading ?
					<LoadingIcon />

					:

					<div>
						<CAInput
							dataTest="search-input"
							icon="SVGSearch"
							placeholder={this.filterText}
							sustain-label
							v-model={this.searchInput}
						/>

						<div class="move-data-between-tables__container">
							<div class="move-data-between-tables__data-table">
								<p>{this.selectedAddIds.length} {this.dataType} selected</p>

								<DataTable
									data={this.tableData}
									dataTest="data-table-data"
									isStickyHeader={this.isStickyHeader}
									itemsPerPage={this.itemsPerPage}
									message={this.tableData.rows.length === 0 ? `No ${this.dataType}` : ''}
									searchTerm={this.searchInput}
									showCheckboxes={this.showCheckboxes}
									showPagination={this.showPagination}
									sortable={this.sortable}
									tableLabel={this.dataType}
									v-model={this.selectedAddIds}
									onSelected={this.addSelectedDataItems}
								/>
							</div>

							{this.tableData.rows.length > 0 &&
								<div class="move-data-between-tables__actions">
									<CAButton
										class="mb-2"
										dataTest="data-table-select-button"
										disabled={this.selectedAddIds.length === 0}
										text={`Select ${this.buttonText}`}
										onClick={this.addSelected}
									>
										{{ rightIcon: () => <CAIcon icon="SVGChevronRight" /> }}
									</CAButton>

									<CAButton
										dataTest="data-table-remove-button"
										disabled={this.selectedRemoveIds.length === 0}
										icon="SVGChevronLeft"
										text={`Remove ${this.buttonText}`}
										onClick={this.removeSelected}
									/>
								</div>
							}

							{this.tableData.rows.length > 0 &&
								<div class="move-data-between-tables__data-table">
									<p class="move-data-between-tables__data-selected">
										{this.selectedData.rows.length > 0 ?
											<span>{this.selectedRemoveIds.length} {this.dataType} selected</span>
											:
											<span />
										}
									</p>

									<DataTable
										data={this.selectedTableData}
										dataTest="data-table-selected-data"
										isStickyHeader={this.isStickyHeader}
										itemsPerPage={this.itemsPerPage}
										message={this.selectedTableData.rows.length === 0 ? `No ${this.dataType} selected` : ''}
										showCheckboxes={this.showCheckboxes}
										showPagination={this.showPagination}
										sortable={this.sortable}
										tableLabel={`Selected ${this.dataType}`}
										onSelected={this.removeSelectedDataItems}
									/>
								</div>
							}
						</div>
					</div>
				}
			</div>
		)
	},
})

interface Data {
	searchInput: string
	selectedAddIds: string[]
	selectedData: TableData
	selectedRemoveIds: string[]
}
